import React, { ReactNode } from "react";
import styled, { useTheme } from "styled-components";
import {
  DisplayCurrency,
  FormattedChartDataPoint,
  LanguageNameSpaces,
} from "@shared/types";
import { showListQueryEmptyState } from "@shared/common/utils";
import { hexToRGB } from "@shared/utils/misc";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import LineChart from "common/Charts/LineChart/LineChart.tsx";
import BarChart from "common/Charts/BarChart.tsx";
import { DISPLAY_CURRENCIES } from "common/constants";
import {
  directionColor,
  getNewLookTokens,
  NEW_LOOK_ON_BRAND,
} from "common/NewLook/tokens.ts";
import { PHONE_BRAND_CHART_EMPTY_ID } from "testIds";

const BRAND_CHART_HEIGHT = 180;
const BRAND_CHART_TICKS = 4;
const BRAND_LINE_WIDTH = 2.5;
const PANEL_TICKS_OPACITY = 0.72;
interface PhoneBrandChartProps {
  data: FormattedChartDataPoint[];
  loading: boolean;
  displayCurrency?: DisplayCurrency;
  precision?: number;
  isSuccess?: boolean;
  surface?: PhoneChartSurface;
}

export type PhoneChartSurface = "brand" | "panel";

interface SurfaceColors {
  line: string;
  up: string;
  down: string;
  ticks: string;
  grid: string;
  muted: string;
}

export const PhoneBrandLineChart = (props: PhoneBrandChartProps) => {
  const colors = useSurfaceColors(props);
  return (
    <BrandChartBox {...props} colors={colors}>
      <LineChart
        {...getBrandChartProps(props, colors)}
        color={colors.line}
        lineWidth={BRAND_LINE_WIDTH}
        includeBounds={true}
      />
    </BrandChartBox>
  );
};

export const PhoneBrandBarChart = (props: PhoneBrandChartProps) => {
  const colors = useSurfaceColors(props);
  return (
    <BrandChartBox {...props} colors={colors}>
      <BarChart
        {...getBrandChartProps(props, colors)}
        usePositiveNegativeColorScheme={true}
        positiveColor={colors.up}
        negativeColor={colors.down}
      />
    </BrandChartBox>
  );
};

const useSurfaceColors = ({
  data,
  surface = "brand",
}: PhoneBrandChartProps): SurfaceColors => {
  const tokens = getNewLookTokens(useTheme());
  if (surface === "brand")
    return {
      line: NEW_LOOK_ON_BRAND.text,
      up: NEW_LOOK_ON_BRAND.up,
      down: NEW_LOOK_ON_BRAND.down,
      ticks: NEW_LOOK_ON_BRAND.ticks,
      grid: NEW_LOOK_ON_BRAND.grid,
      muted: NEW_LOOK_ON_BRAND.muted,
    };
  const last = data[data.length - 1]?.y ?? 0;
  return {
    line: last ? directionColor(tokens, last) : tokens.accent,
    up: tokens.up,
    down: tokens.down,
    ticks: hexToRGB(tokens.muted, PANEL_TICKS_OPACITY) ?? tokens.muted,
    grid: tokens.hairline ?? tokens.muted,
    muted: tokens.muted,
  };
};

const BrandChartBox = ({
  data,
  loading,
  isSuccess,
  colors,
  children,
}: PhoneBrandChartProps & { colors: SurfaceColors; children: ReactNode }) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  return (
    <ChartBox>
      {showListQueryEmptyState(data, loading, isSuccess) ? (
        <Empty
          data-testid={PHONE_BRAND_CHART_EMPTY_ID}
          style={{ color: colors.muted }}
        >
          {t("empty.no_data_collected")}
        </Empty>
      ) : (
        children
      )}
    </ChartBox>
  );
};

const getBrandChartProps = (
  {
    data,
    loading,
    displayCurrency,
    precision,
    isSuccess,
  }: PhoneBrandChartProps,
  colors: SurfaceColors
) => ({
  data,
  loading,
  loadingProps: { backgroundColor: "transparent", spinnerColor: colors.line },
  displayCurrency,
  precision: displayCurrency === DISPLAY_CURRENCIES.USD ? 0 : precision,
  isSuccess,
  height: BRAND_CHART_HEIGHT,
  ticksColor: colors.ticks,
  gridColor: colors.grid,
  numOfXTicks: BRAND_CHART_TICKS,
  numOfYTicks: BRAND_CHART_TICKS,
  axisOverlapData: true,
});

const ChartBox = styled.div`
  height: ${BRAND_CHART_HEIGHT}px;
`;
const Empty = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  font-size: 13px;
  line-height: 18px;
`;
